import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const REASONS = ["EXPIRED", "DAMAGED", "QUALITY_ISSUE", "OTHER_SHRINKAGE"];

/** Abschreibung einer exakten Bestandsdimension. Zulässigkeit, Grund-Validierung und maximale
 *  Menge entscheidet ausschließlich der Server. */
export default function WriteOffDialog({ open, onClose, onDone, row }) {
  const { t, fmtDate, fmtMoney } = useI18n();
  const [reason, setReason] = useState("");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // Stabiler Idempotenz-Schlüssel je Dialog-Öffnung: ein Retry nach Timeout erzeugt keine
  // zweite Abschreibung.
  const [idemKey, setIdemKey] = useState("");

  useEffect(() => {
    if (!open || !row) return;
    setReason(row.status === "expired" ? "EXPIRED" : "");
    setQty(""); setNote("");
    setIdemKey(`wo-${row.variant_id}-${row.lot_id || "nolot"}-${row.storage_location_id}-${Date.now()}`);
  }, [open, row]);

  const value = Number(qty) > 0 && row?.unit_cost != null ? Number(qty) * row.unit_cost : null;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/inventory/write-offs", {
        variant_id: row.variant_id, warehouse_id: row.warehouse_id,
        storage_location_id: row.storage_location_id, lot_id: row.lot_id || null,
        quality: row.quality, quantity: Number(qty), reason_code: reason, reason_text: note,
        idempotency_key: `${idemKey}-${qty}-${reason}`,
      });
      toast.success(t("wo.done", { qty: res.data.quantity, value: fmtMoney(res.data.total_cost) }));
      onDone?.(); onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const noteMissing = reason === "OTHER_SHRINKAGE" && !note.trim();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="write-off-dialog">
        <DialogHeader><DialogTitle>{t("wo.title")} · {row?.product_name || row?.sku}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">{t("wo.subtitle")}</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-border bg-muted/40 p-3 text-xs"
          data-testid="write-off-context">
          <div>{t("inv.productSku")}: <span className="font-mono">{row?.sku}</span></div>
          <div>{t("ex.supplierLot")}: <span className="font-mono">{row?.lot_number || t("wo.noLot")}</span></div>
          <div>{t("ex.supplier")}: {row?.supplier_name || "—"}</div>
          <div>{t("ex.receipt")}: {row?.receipt_reference || "—"}</div>
          <div>{t("ex.bbd")}: {row?.best_before_date ? fmtDate(row.best_before_date) : "—"}</div>
          <div>{t("ex.daysLeft")}: {row?.days_left == null ? "—" : row.days_left}</div>
          <div>{t("ex.warehouse")}: {row?.warehouse_name || "—"}</div>
          <div>{t("ex.location")}: <span className="font-mono">{row?.location_code || "—"}</span></div>
          <div>{t("ex.quality")}: {t(`quality.${row?.quality}`, row?.quality)}</div>
          <div>{t("wo.available")}: <span className="font-mono font-semibold">{row?.quantity}</span></div>
        </div>
        <div className="space-y-3">
          <div>
            <Label>{t("wo.reason")}</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1.5" data-testid="write-off-reason"><SelectValue placeholder={t("wo.reason")} /></SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`write-off-reason-${r}`}>{t(`wo.reason.${r}`)}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("wo.quantity")}</Label>
            <NumberInput className="mt-1.5" value={qty} onChange={setQty} testId="write-off-qty" />
          </div>
          <div>
            <Label>{t("wo.note")}</Label>
            <Input className="mt-1.5" value={note} data-testid="write-off-note"
              onChange={(e) => setNote(e.target.value)} />
            {noteMissing && <p className="mt-1 text-xs text-destructive" data-testid="write-off-note-required">{t("wo.noteRequired")}</p>}
          </div>
          <div className="flex justify-between rounded-md border border-border p-3 text-sm">
            <span className="text-muted-foreground">{t("wo.unitCost")}: <span className="font-mono text-foreground">{row?.unit_cost != null ? fmtMoney(row.unit_cost) : "—"}</span></span>
            <span className="font-medium" data-testid="write-off-value">{t("wo.totalCost")}: {value == null ? "—" : fmtMoney(value)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="destructive" onClick={submit} data-testid="write-off-submit"
            disabled={busy || !reason || !(Number(qty) > 0) || noteMissing}>{t("wo.confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
